import { useEffect, useMemo, useRef, useState } from 'react';
import { Outlet, useParams } from 'react-router-dom';
import { useAppSelector, useAppDispatch } from '@store';
import {
  Filters,
  Table,
  useFilters,
  useQueryState,
  useScroll,
  useGetList,
  SchemaFilterType,
  formatDate,
  PermissionDenied,
  PermissionEnum,
  IconPower,
  IconLoader,
} from '@ui/src';
import {
  useLazyGetCustomerDevicesQuery,
  useEndCustomerSessionMutation,
} from '../store/services/individual';
import {
  schema,
  columns,
  columnsLabels,
  filtersSchema,
} from '../variables/devices';
import { Device } from '../types';
import { setTakeCount } from '../store/slices/devices';

import cn from '../information.module.css';

const formatDevicesList = (
  list: Device[],
  activePermissions: PermissionEnum[],
  onEndSessions: (id: string) => void,
  endSessionInProgress?: boolean,
  selectedDeviceId?: string,
): Device[] => {
  const nextList = list?.map((item) => {
    const result = { ...item };

    const render: Record<string, React.JSX.Element> = {};
    render.createdAt = (
      <div>{result.createdAt ? formatDate(result.createdAt, true) : '-'}</div>
    );
    render.ip = (
      <div className={cn.ipRow}>
        <div className={cn.ipRowValue}>{result.ip}</div>
        {activePermissions?.includes(
          PermissionEnum.CUSTOMERS_SESSIONS_DELETE_ACTION,
        ) && (
          <button
            type="button"
            className={cn.endSessionButton}
            aria-label="End all session button"
            disabled={Boolean(
              endSessionInProgress && selectedDeviceId === result.id,
            )}
            onClick={() => onEndSessions(result.id)}
          >
            {endSessionInProgress && selectedDeviceId === result.id ? (
              <IconLoader width={16} height={16} />
            ) : (
              <IconPower />
            )}
          </button>
        )}
      </div>
    );

    result.render = render;

    return result;
  });

  return nextList;
};

const IndividualCustomerDevices = () => {
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const dispatch = useAppDispatch();

  const {
    individualCustomers: {
      info: {
        devices: { devices, takeCount, pagination },
      },
    },
    root: { activePermissions },
  } = useAppSelector((state) => state);
  const [selectedDeviceId, setSelectedDeviceId] = useState('');

  const [getList, { isFetching, isLoading }] = useLazyGetCustomerDevicesQuery();
  const [
    endSessions,
    { isLoading: endSessionInProgress, isSuccess: endSessionsIsSuccess },
  ] = useEndCustomerSessionMutation();

  const { filters, sort, onSortChange, onFilterChange } = useQueryState(schema);

  const { customerId } = useParams();

  const { getListAction, setPage, onFilter, errorList } = useGetList<Device>({
    getList,
    onFilterChange,
    onSortChange,
    schema,
    itemId: customerId,
  });

  const { filtersComponent, handleSubmit } = useFilters({
    filtersSchema,
    filters,
    onFilterChange: onFilter,
  });

  const onEndSessions = (id: string) => {
    setSelectedDeviceId(id);

    endSessions({
      id: customerId as string,
      deviceId: id,
    });
  };

  const formattedData = useMemo(() => {
    const data = formatDevicesList(
      devices as Device[],
      activePermissions as PermissionEnum[],
      onEndSessions,
      endSessionInProgress,
      selectedDeviceId,
    );

    return data;
  }, [devices, sort, filters, activePermissions, endSessionInProgress]);

  useScroll({
    element: scrollRef,
    requestInProgress: isFetching,
  });

  useEffect(() => {
    if (endSessionsIsSuccess) {
      setSelectedDeviceId('');
    }
  }, [endSessionsIsSuccess]);

  useEffect(
    () => () => {
      setSelectedDeviceId('');
    },
    [],
  );

  useEffect(() => {
    if (scrollRef && scrollRef.current) {
      const { offsetHeight } = scrollRef.current;

      if (offsetHeight) {
        const take = offsetHeight / 58;

        dispatch(setTakeCount(Math.ceil(take + 3)));
      }
    }
  }, [scrollRef]);

  useEffect(() => {
    if (
      filters &&
      !devices &&
      takeCount &&
      takeCount !== 0 &&
      activePermissions?.includes(PermissionEnum.EMPLOYEES_DEVICES_LIST)
    ) {
      const nextFilters: Filters = {
        ...filters,
        take: {
          value: [takeCount?.toString()],
          type: SchemaFilterType.STRING,
        },
      };

      getListAction({
        filters: nextFilters,
      });
    }
  }, [filters, devices, takeCount, activePermissions]);

  let content;

  if (!activePermissions?.includes(PermissionEnum.CUSTOMERS_DEVICES_LIST)) {
    content = <PermissionDenied />;
  } else {
    content = (
      <Table
        columns={columns}
        data={formattedData || []}
        onSort={() => {}}
        filters={filters}
        sort={{}}
        columnsLabels={columnsLabels}
        tableClassName={!devices?.length ? cn.devicesEmpty : ''}
        tableTopClassName={cn.devicesTableTop}
        onFilterChange={() => {}}
        filtersComponent={filtersComponent}
        handleSubmit={handleSubmit}
        isLoading={isLoading || isFetching}
        take={takeCount?.toString()}
        error={errorList}
        setPage={(data) => {
          setPage({
            ...data,
            filters: {
              ...filters,
            },
          });
        }}
        pagination={pagination}
        clickable
        withScroll
      />
    );
  }

  return (
    <div className={cn.devicesContent} ref={scrollRef}>
      {content}
      <Outlet />
    </div>
  );
};

export default IndividualCustomerDevices;
